import { Phone, ExternalLink, AlertTriangle } from "lucide-react";

/**
 * Contact pieces shared by the directory cards (light surface) and the map detail panel (dark surface), so a phone
 * number is rendered by exactly one component. Everything comes from the normalized model in
 * src/lib/utilitiesDirectory.js, which has already dropped numbers without a confidence value.
 */

const TONE = {
  light: {
    label: "text-slate-600",
    number: "text-[#0A2540]",
    meta: "text-slate-600",
    row: "border-slate-200",
    warn: "text-rose-700",
    link: "text-[#0A2540] underline decoration-slate-300 hover:decoration-[#0A2540]",
    icon: "text-[#16A34A]",
    focus: "focus-visible:ring-[#0A2540] focus-visible:ring-offset-white",
  },
  dark: {
    label: "text-slate-300",
    number: "text-white",
    meta: "text-slate-300",
    row: "border-white/10",
    warn: "text-rose-300",
    link: "text-[#4ADE80] underline decoration-white/30 hover:decoration-[#4ADE80]",
    icon: "text-[#4ADE80]",
    focus: "focus-visible:ring-white focus-visible:ring-offset-[#0A2540]",
  },
};

function Call911Note({ t }) {
  return (
    <p className={`flex items-start gap-1.5 text-xs font-semibold ${t.warn}`} data-testid="utility-call-911">
      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      For a downed line or if anyone is in danger, call 911 first.
    </p>
  );
}

/**
 * Tap-to-call list. `compact` (map panel) shows each number's short label. `showSources` adds the data file's source links.
 */
export function PhoneList({ utility, tone = "light", compact = false, showSources = false, testId = "utility-phones" }) {
  const t = TONE[tone];
  const phones = utility.phones;

  if (phones.length === 0) {
    return (
      <div data-testid={`${testId}-empty`}>
        <p className={`text-sm leading-relaxed ${t.meta}`}>
          Phone numbers for {utility.name} are not listed here yet.
          {utility.website ? " Use the website link below for current contact details." : ""}
        </p>
      </div>
    );
  }

  return (
    <ul className="m-0 list-none p-0" data-testid={testId}>
      {phones.map((p) => (
        <li key={p.key} className={`border-t first:border-t-0 ${compact ? "py-1" : "py-2"} ${t.row}`} data-testid="utility-phone" data-kind={p.kind}>
          <p className={`break-words text-xs font-semibold leading-snug ${t.label}`}>{compact ? p.shortLabel : p.label}</p>
          <p className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5">
            <a
              href={p.href}
              aria-label={`Call ${utility.name}, ${p.label.toLowerCase()}: ${p.number}`}
              className={`inline-flex min-h-[44px] items-center gap-2 rounded-md font-mono text-base font-bold ${t.number} focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${t.focus}`}
              data-testid="utility-phone-link"
            >
              <Phone className={`h-4 w-4 shrink-0 ${t.icon}`} aria-hidden="true" />
              {p.number}
            </a>
            {p.hours && <span className={`min-w-0 break-words text-xs ${t.meta}`}>{p.hours}</span>}
          </p>
          {!compact && p.call911First && <Call911Note t={t} />}
          {p.lowConfidence && <p className={`text-xs ${t.meta}`}>Not fully confirmed. Check the website before relying on it.</p>}
          {showSources && p.sources.length > 0 && (
            <p className={`text-xs ${t.meta}`}>
              Source:{" "}
              {p.sources.map((s, i) => (
                <span key={s.href}>
                  {i > 0 && ", "}
                  <a href={s.href} target="_blank" rel="noopener noreferrer" className={t.link}>
                    {s.host}
                  </a>
                </span>
              ))}
            </p>
          )}
        </li>
      ))}
      {compact && phones.some((p) => p.call911First) && (
        <li className="py-2">
          <Call911Note t={t} />
        </li>
      )}
    </ul>
  );
}

/** Website / outage page / outage map links. Absent links are simply not rendered. */
/** `compact` uses short link text so all three links fit on one row in the narrow map panel. */
export function UtilityLinks({ utility, tone = "light", compact = false, testId = "utility-links" }) {
  const t = TONE[tone];
  const links = [
    utility.outageMapUrl && { href: utility.outageMapUrl, text: "Outage map", label: `${utility.name} outage map (opens in a new tab)`, id: "outage-map" },
    utility.outageUrl && { href: utility.outageUrl, text: compact ? "Report outage" : "Report or check an outage", label: `${utility.name} outage page (opens in a new tab)`, id: "outage-page" },
    utility.website && { href: utility.website, text: "Website", label: `${utility.name} website (opens in a new tab)`, id: "website" },
  ].filter(Boolean);
  if (links.length === 0) return null;
  return (
    <ul className="m-0 flex list-none flex-wrap gap-x-5 gap-y-1 p-0 text-sm font-semibold" data-testid={testId}>
      {links.map((l) => (
        <li key={l.id}>
          <a
            href={l.href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={l.label}
            className={`inline-flex min-h-[44px] items-center gap-1.5 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${t.link} ${t.focus}`}
            data-testid={`utility-link-${l.id}`}
          >
            {l.text}
            <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
          </a>
        </li>
      ))}
    </ul>
  );
}
